import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';
import toast from 'react-hot-toast';
import type { ApiError, ApiResponse, AuthTokens } from '@types';

// Base URL set to /api - Vite proxy forwards /api/* to backend
// API files should use paths like /v1/auth/login (baseURL + path = /api/v1/auth/login)
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Track if we're currently refreshing to prevent multiple refresh attempts
let isRefreshing = false;
// Track if auth failure has been handled to prevent multiple toasts/redirects
let authFailureHandled = false;
let failedQueue: Array<{
  resolve: (token: string | null) => void;
  reject: (error: Error) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Create base axios instance
const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
axiosInstance.interceptors.request.use(
  config => {
    const token = localStorage.getItem('access_token');
    if (token) {
      // Only set from localStorage if not already set (retry case sets it manually)
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

/**
 * Attempt to refresh the access token using the refresh token.
 * Returns the new access token or null if refresh fails.
 */
const refreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    return null;
  }

  try {
    // Use axios directly to avoid interceptors
    const response = await axios.post<ApiResponse<AuthTokens>>(
      `${API_BASE_URL}/v1/auth/refresh`,
      { refresh_token: refreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    );

    // The response structure depends on whether the backend wraps in ApiResponse
    const data = response.data;
    const tokens = data.success ? data.data : (data as unknown as AuthTokens);

    if (tokens?.access_token) {
      localStorage.setItem('access_token', tokens.access_token);
      if (tokens.refresh_token) {
        localStorage.setItem('refresh_token', tokens.refresh_token);
      }
      return tokens.access_token;
    }
    return null;
  } catch {
    // Token refresh failed - return null to trigger re-authentication
    return null;
  }
};

/**
 * Clear auth tokens and notify user of session expiry.
 * Uses flag to prevent multiple toasts/redirects from concurrent requests.
 */
const handleAuthFailure = () => {
  if (authFailureHandled) {
    return; // Already handled, skip duplicate toasts
  }
  authFailureHandled = true;
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  toast.error('Session expired. Please login again.');

  // Reset the flag after a short delay to allow future auth failures
  setTimeout(() => {
    authFailureHandled = false;
  }, 1000);
};

// Response interceptor - returns response.data directly
axiosInstance.interceptors.response.use(
  response => response.data,
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (error.response) {
      const { status, data } = error.response;

      // Handle 401 errors
      if (status === 401 && originalRequest) {
        // If this was already a retry, the refresh token is invalid - clear auth and stop
        if (originalRequest._retry) {
          handleAuthFailure();
          return Promise.reject(error);
        }
        // Don't retry auth endpoints (login, register, refresh)
        const authEndpoints = [
          '/auth/login',
          '/auth/register',
          '/auth/refresh',
        ];
        const isAuthEndpoint = authEndpoints.some(ep =>
          originalRequest.url?.includes(ep)
        );

        if (isAuthEndpoint) {
          // Auth endpoint failed - clear tokens and reject
          handleAuthFailure();
          return Promise.reject(error);
        }

        // Mark request as retried to prevent infinite loop
        originalRequest._retry = true;

        if (isRefreshing) {
          // If already refreshing, queue this request
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(token => {
              if (token) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return axiosInstance(originalRequest);
              }
              return Promise.reject(error);
            })
            .catch(err => Promise.reject(err));
        }

        isRefreshing = true;

        try {
          const newToken = await refreshAccessToken();

          if (newToken) {
            // Refresh successful - retry original request and process queue
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            processQueue(null, newToken);
            return axiosInstance(originalRequest);
          } else {
            // Refresh failed - clear tokens and reject all queued requests
            handleAuthFailure();
            processQueue(new Error('Token refresh failed'), null);
            return Promise.reject(error);
          }
        } catch (refreshError) {
          handleAuthFailure();
          processQueue(refreshError as Error, null);
          return Promise.reject(error);
        } finally {
          isRefreshing = false;
        }
      }

      // Handle other status codes
      switch (status) {
        case 403:
          toast.error('You do not have permission to perform this action.');
          break;

        case 404: {
          // Don't show toast for 404 on endpoints where "not found" is expected
          // e.g., user has no subscription yet, no business profile, etc.
          const silentPaths = [
            '/profile',
            '/invoices',
            '/subscriptions/current',
            '/subscriptions/usage',
            '/business/profile',
          ];
          const isSilentPath = silentPaths.some(path =>
            originalRequest?.url?.includes(path)
          );
          if (!isSilentPath) {
            toast.error('The requested resource was not found.');
          }
          break;
        }

        case 429:
          toast.error('Too many requests. Please slow down.');
          break;

        case 500:
          toast.error('Server error. Please try again later.');
          break;

        default:
          if (data?.message) {
            toast.error(data.message);
          }
      }
    } else if (error.request) {
      toast.error('No response from server. Please check your connection.');
    } else {
      toast.error('An unexpected error occurred.');
    }

    return Promise.reject(error);
  }
);

/**
 * Typed API client that reflects the response interceptor behavior.
 * The interceptor returns response.data, so our methods return T directly.
 */
export const apiClient = {
  get: <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    axiosInstance.get(url, config) as Promise<T>,

  post: <T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> => axiosInstance.post(url, data, config) as Promise<T>,

  put: <T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> => axiosInstance.put(url, data, config) as Promise<T>,

  patch: <T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> => axiosInstance.patch(url, data, config) as Promise<T>,

  delete: <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    axiosInstance.delete(url, config) as Promise<T>,
};

// Helper function to handle API responses
export const handleApiResponse = <T>(response: ApiResponse<T>): T => {
  if (response.success) {
    return response.data;
  }
  throw new Error(response.error);
};

export default apiClient;
